import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { propertyAPI, facturacionAPI, API } from '@/services/api';
import { FileText, Plus, Download, ArrowRightLeft, Coffee } from 'lucide-react';
import { toast } from 'sonner';

const money = (v) => `$${Math.round(v || 0).toLocaleString('es-CO')}`;

const TAX_DEFS = [
  { code: 'iva', label: 'IVA', op: 'suma' },
  { code: 'reteiva', label: 'ReteIVA', op: 'resta' },
  { code: 'reteica', label: 'ReteICA', op: 'resta' },
  { code: 'retefuente', label: 'Retefuente', op: 'resta' },
];

const emptyForm = {
  reservation_id: '', cliente_tipo: 'natural', cliente_nombre: '', cliente_razon_social: '',
  cliente_tipo_documento: 'CC', cliente_documento: '', cliente_email: '',
  incluye_desayuno: false, descripcion_extra: '', num_noches: 1, valor_noche: 0,
  impuestos_aplicados: ['iva'],
};

export default function FacturacionDocsPage({ tipo = 'remision' }) {
  const isRemision = tipo === 'remision';
  const titulo = isRemision ? 'Remisiones' : 'Facturas DIAN';
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId] = useGlobalProperty(properties);
  const [docs, setDocs] = useState([]);
  const [billable, setBillable] = useState([]);
  const [taxesCfg, setTaxesCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    propertyAPI.getAll().then(r => setProperties(r.data)).catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    if (!selectedPropertyId) return;
    setLoading(true);
    try {
      const [docsRes, billRes, cfgRes] = await Promise.all([
        facturacionAPI.getDocs(selectedPropertyId, tipo),
        facturacionAPI.getBillable(selectedPropertyId),
        facturacionAPI.getConfig(selectedPropertyId),
      ]);
      setDocs(docsRes.data);
      setBillable(billRes.data);
      setTaxesCfg(cfgRes.data.taxes);
    } catch (e) {
      toast.error('Error al cargar datos de facturación');
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyId, tipo]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openDialog = () => {
    const defaults = TAX_DEFS.filter(t => taxesCfg?.[t.code]?.enabled).map(t => t.code);
    setForm({ ...emptyForm, impuestos_aplicados: defaults.length ? defaults : ['iva'] });
    setOpen(true);
  };

  const onSelectReservation = (id) => {
    const r = billable.find(b => b.id === id);
    if (!r) {
      setForm(f => ({ ...f, reservation_id: '' }));
      return;
    }
    setForm(f => ({
      ...f,
      reservation_id: id,
      cliente_nombre: `${r.guest_name} ${r.guest_lastname}`.trim(),
      cliente_documento: r.guest_documento || '',
      cliente_tipo_documento: r.guest_tipo_documento || 'CC',
      cliente_email: r.guest_email || '',
      num_noches: r.num_noches,
      valor_noche: r.precio_por_noche,
    }));
  };

  const toggleTax = (code) => {
    setForm(f => ({
      ...f,
      impuestos_aplicados: f.impuestos_aplicados.includes(code)
        ? f.impuestos_aplicados.filter(c => c !== code)
        : [...f.impuestos_aplicados, code],
    }));
  };

  const subtotal = (parseInt(form.num_noches) || 0) * (parseFloat(form.valor_noche) || 0);
  const ivaVal = form.impuestos_aplicados.includes('iva') ? subtotal * ((taxesCfg?.iva?.pct ?? 19) / 100) : 0;
  const taxLines = form.impuestos_aplicados.map(code => {
    const def = TAX_DEFS.find(t => t.code === code);
    const pct = taxesCfg?.[code]?.pct ?? 0;
    const base = code === 'reteiva' ? ivaVal : subtotal;
    return { code, label: def.label, op: def.op, pct, valor: base * pct / 100 };
  });
  const total = subtotal + taxLines.filter(t => t.op === 'suma').reduce((s, t) => s + t.valor, 0) - taxLines.filter(t => t.op === 'resta').reduce((s, t) => s + t.valor, 0);

  const handleCreate = async () => {
    if (!form.cliente_nombre.trim()) { toast.error('Ingresa el nombre del cliente'); return; }
    if (!form.num_noches || !form.valor_noche) { toast.error('Noches y valor por noche son requeridos'); return; }
    setCreating(true);
    try {
      const payload = {
        ...form,
        tipo,
        reservation_id: form.reservation_id || null,
        num_noches: parseInt(form.num_noches),
        valor_noche: parseFloat(form.valor_noche),
      };
      const res = await facturacionAPI.createDoc(selectedPropertyId, payload);
      toast.success(`${isRemision ? 'Remisión' : 'Factura'} ${res.data.numero} creada`);
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el documento');
    } finally {
      setCreating(false);
    }
  };

  const openPdf = (doc) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/facturacion/documentos/${doc.id}/pdf?auth=${token}`, '_blank');
  };

  const handleConvert = async (doc) => {
    setConverting(doc.id);
    try {
      const res = await facturacionAPI.convertToFactura(doc.id);
      toast.success(`Factura ${res.data.numero} generada desde la remisión ${doc.numero}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al convertir');
    } finally {
      setConverting(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid={`facturacion-${tipo}-page`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid={`fact-${tipo}-title`}>
              {titulo}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isRemision
                ? 'Documentos previos a la factura electrónica, generados al check-out'
                : 'Facturas listas para transmitir a la DIAN (integración Siigo próximamente)'}
            </p>
          </div>
          <Button onClick={openDialog} className="bg-slate-800 hover:bg-slate-700" data-testid={`new-${tipo}-btn`}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva {isRemision ? 'Remisión' : 'Factura'}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documentos generados
            </CardTitle>
            <CardDescription>{docs.length} documento(s)</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            ) : docs.length === 0 ? (
              <p className="text-center text-muted-foreground py-10" data-testid={`fact-${tipo}-empty`}>
                No hay {isRemision ? 'remisiones' : 'facturas'} generadas todavía
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`fact-${tipo}-table`}>
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-slate-500">
                      <th className="py-2 pr-4">Número</th>
                      <th className="py-2 pr-4">Fecha</th>
                      <th className="py-2 pr-4">Cliente</th>
                      <th className="py-2 pr-4 text-right">Total</th>
                      <th className="py-2 pr-4">Estado</th>
                      <th className="py-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map(doc => (
                      <tr key={doc.id} className="border-b hover:bg-slate-50" data-testid={`fact-doc-row-${doc.numero}`}>
                        <td className="py-3 pr-4 font-mono font-semibold">{doc.numero}</td>
                        <td className="py-3 pr-4">{(doc.fecha || '').slice(0, 10)}</td>
                        <td className="py-3 pr-4">
                          {doc.cliente_tipo === 'juridica' ? (doc.cliente_razon_social || doc.cliente_nombre) : doc.cliente_nombre}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold">{money(doc.total)}</td>
                        <td className="py-3 pr-4">
                          {isRemision ? (
                            doc.estado_dian === 'convertida' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Facturada ({doc.factura_numero})</Badge>
                            ) : (
                              <Badge className="bg-sky-100 text-sky-700 border-sky-200">Remisión</Badge>
                            )
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pendiente Siigo</Badge>
                          )}
                        </td>
                        <td className="py-3 text-right space-x-2">
                          <Button size="sm" variant="outline" onClick={() => openPdf(doc)} data-testid={`fact-pdf-${doc.numero}`}>
                            <Download className="h-4 w-4 mr-1" /> PDF
                          </Button>
                          {isRemision && doc.estado_dian !== 'convertida' && (
                            <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => handleConvert(doc)} disabled={converting === doc.id}
                              data-testid={`fact-convert-${doc.numero}`}>
                              <ArrowRightLeft className="h-4 w-4 mr-1" />
                              {converting === doc.id ? 'Convirtiendo...' : 'Convertir a Factura'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog nueva remisión/factura */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nueva {isRemision ? 'Remisión' : 'Factura'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              {/* Reserva */}
              <div className="space-y-2">
                <Label>Reserva (opcional — trae datos del check-out)</Label>
                <Select value={form.reservation_id} onValueChange={onSelectReservation}>
                  <SelectTrigger data-testid="fact-reserva-select"><SelectValue placeholder="Seleccionar reserva..." /></SelectTrigger>
                  <SelectContent>
                    {billable.map(r => (
                      <SelectItem key={r.id} value={r.id} data-testid={`fact-reserva-opt-${r.id}`}>
                        {r.guest_name} {r.guest_lastname} — {r.room_nombre} ({r.fecha_checkin?.slice(0,10)} → {r.fecha_checkout?.slice(0,10)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Cliente */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">Datos del cliente</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tipo de cliente</Label>
                    <Select value={form.cliente_tipo} onValueChange={(v) => setForm(f => ({ ...f, cliente_tipo: v }))}>
                      <SelectTrigger data-testid="fact-cliente-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="natural">Persona natural</SelectItem>
                        <SelectItem value="juridica">Empresa (jurídica)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.cliente_tipo === 'juridica' && (
                    <div className="space-y-2">
                      <Label>Razón social de la empresa</Label>
                      <Input value={form.cliente_razon_social} onChange={(e) => setForm(f => ({ ...f, cliente_razon_social: e.target.value }))} placeholder="Empresa S.A.S." data-testid="fact-cliente-razon" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>{form.cliente_tipo === 'juridica' ? 'Nombre del huésped' : 'Nombre completo'}</Label>
                    <Input value={form.cliente_nombre} onChange={(e) => setForm(f => ({ ...f, cliente_nombre: e.target.value }))} data-testid="fact-cliente-nombre" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label>Doc.</Label>
                      <Select value={form.cliente_tipo_documento} onValueChange={(v) => setForm(f => ({ ...f, cliente_tipo_documento: v }))}>
                        <SelectTrigger data-testid="fact-cliente-tipodoc"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CC">CC</SelectItem>
                          <SelectItem value="NIT">NIT</SelectItem>
                          <SelectItem value="CE">CE</SelectItem>
                          <SelectItem value="Pasaporte">Pasaporte</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Número de documento</Label>
                      <Input value={form.cliente_documento} onChange={(e) => setForm(f => ({ ...f, cliente_documento: e.target.value }))} data-testid="fact-cliente-documento" />
                    </div>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Email (opcional)</Label>
                    <Input type="email" value={form.cliente_email} onChange={(e) => setForm(f => ({ ...f, cliente_email: e.target.value }))} data-testid="fact-cliente-email" />
                  </div>
                </div>
              </div>

              {/* Hospedaje */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">Detalle del hospedaje</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Noches</Label>
                    <Input type="number" min="1" value={form.num_noches} onChange={(e) => setForm(f => ({ ...f, num_noches: e.target.value }))} data-testid="fact-noches" />
                  </div>
                  <div className="space-y-2">
                    <Label>Valor por noche (COP)</Label>
                    <Input type="number" min="0" value={form.valor_noche} onChange={(e) => setForm(f => ({ ...f, valor_noche: e.target.value }))} data-testid="fact-valor-noche" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.incluye_desayuno} onCheckedChange={(v) => setForm(f => ({ ...f, incluye_desayuno: !!v }))} data-testid="fact-desayuno" />
                  <Coffee className="h-4 w-4 text-amber-600" />
                  <span className="text-sm">Incluye desayuno</span>
                </label>
                <div className="space-y-2">
                  <Label>Descripción adicional (opcional)</Label>
                  <Textarea value={form.descripcion_extra} onChange={(e) => setForm(f => ({ ...f, descripcion_extra: e.target.value }))} rows={2} placeholder="Ej: Servicios adicionales, consumos del minibar..." data-testid="fact-descripcion-extra" />
                </div>
              </div>

              {/* Impuestos */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">Impuestos y retenciones (manual por documento)</h4>
                <div className="grid grid-cols-2 gap-2">
                  {TAX_DEFS.map(t => (
                    <label key={t.code} className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-slate-50" data-testid={`fact-tax-opt-${t.code}`}>
                      <Checkbox checked={form.impuestos_aplicados.includes(t.code)} onCheckedChange={() => toggleTax(t.code)} />
                      <span className="text-sm">{t.label} ({taxesCfg?.[t.code]?.pct ?? 0}%)</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Totales */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm" data-testid="fact-totales-preview">
                <div className="flex justify-between"><span>Subtotal ({form.num_noches} noche(s) × {money(form.valor_noche)}):</span><span className="font-semibold">{money(subtotal)}</span></div>
                {taxLines.map(t => (
                  <div key={t.code} className="flex justify-between text-slate-600">
                    <span>{t.label} ({t.pct}%):</span>
                    <span>{t.op === 'suma' ? '+' : '-'} {money(t.valor)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t pt-2 mt-2 text-base font-bold">
                  <span>TOTAL:</span>
                  <span data-testid="fact-total-preview">{money(total)}</span>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={handleCreate} disabled={creating} className="bg-slate-800 hover:bg-slate-700" data-testid="fact-create-submit">
                  {creating ? 'Creando...' : `Crear ${isRemision ? 'Remisión' : 'Factura'}`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
